/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, ViewChild, ElementRef, Input, forwardRef, OnInit, Output, EventEmitter } from '@angular/core';
import { SearchableProvider } from '../../../model/interface/searchable.provider';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputType } from 'src/app/model/enums/input.type';
import {LogService } from 'src/app/services';
import {CommaPipe} from "../../pipes/comma.pipe";

const noop = (): any => {};

@Component({
  selector: 'input-data',
  templateUrl: 'input-data.component.html',
  styleUrls: ['input-data.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputDataComponent),
      multi: true
    }
  ],
})

export class InputDataComponent implements ControlValueAccessor, OnInit {
  @ViewChild('inputText') inputRef: ElementRef;
  @Input() provider: SearchableProvider;
  @Input() id: string;
  @Input() fieldName: string;
  @Input() type: InputType;
  @Input() allowZero = false;
  @Input() allowNegative = true;
  @Input() name: string;
  @Input() disabled = false;
  @Input() showButton = true;
  @Input() placeholder = 'Type anything you want';
  @Input() inputMask;
  @Input() minLength = 1;
  @Input() maxLength = 100;
  @Input() email = false;
  @Input() required = false;
  @Input() readonly = false;
  @Output() onValueTouched: EventEmitter<boolean> = new EventEmitter();
  @Output() onSaveSuccess: EventEmitter<void> = new EventEmitter<void>();

  inputValue = '';
  valueChangedFlag = false;
  warningFlag = false;
  errorFlag = false;
  shadowCopy: any;

  private onChange: (_: any) => void = noop;
  private onTouched: () => void = noop;

  constructor(
    private currencyPipe: CommaPipe,
    private logService: LogService
  ) {
  }

  get value(): any {
    return this.inputValue;
  }

  set value(val) {
    this.inputValue = val;
    this.onChange(val);
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  writeValue(value): void {
    this.onShadowCopy(value);
    if (this.type === InputType.MONEY) {
      this.inputValue = this.currencyPipe.transform(value);
    } else if (this.type === InputType.NUMBER) {
      this.inputValue = value;
    } else this.inputValue = value;
  }

  ngOnInit(): void {
  }

  accept(): void {
    if (this.inputValue === undefined && this.required) {
      this.errorFlag = true;
      this.warningFlag = false;
      return;
    }

    if (this.email) {
      this.warningFlag = true;
      this.errorFlag = false;
      return;
    }

    if (this.required) {
      if (this.inputValue.toString().length < this.minLength || this.inputValue.toString().length > this.maxLength) {
        this.errorFlag = true;
        this.warningFlag = false;
        return;
      }
    }

    if (this.provider) {
      let value: any;
      if (this.type === InputType.MONEY)
        value = Number.parseFloat(this.currencyPipe.parse(this.inputValue));
      else if (this.type === InputType.NUMBER)
        value = Number(this.inputValue);
      else
        value = this.inputValue;

      this.provider.save(this.id, this.fieldName, value)
        .subscribe(data => {
          if (data.success) {
            this.finalize();
            this.onShadowCopy(this.inputValue);
            this.onSaveSuccess.emit();
            this.warningFlag = false;
            this.errorFlag = false;
          } else this.logService.log(JSON.stringify(data, null, 2));
        });
    } else {
      this.finalize();
    }
  }


  clear(): void {

    this.inputValue = this.getShadowCopy();
    this.finalize();
    this.warningFlag = false;
    this.errorFlag = false;

  }

  valueChanged(): void {
    this.valueChangedFlag = true;

    // Propagate Changes to ngModel
    this.onChange(this.inputValue);

    this.onValueTouched.emit(this.valueChangedFlag);
  }

  onFocus(): void {
    if (this.type === InputType.MONEY) {
      if ((!this.allowZero ? this.inputValue === '0' : false)) {
        this.inputValue = '';
      } else {
        this.inputValue = this.currencyPipe.parse(this.inputValue);
        this.onChange(this.inputValue);
      }
    }
  }

  onBlur(): void {
    if (this.type === InputType.MONEY) {
      if (this.inputValue === '-' || this.inputValue === '' || (!this.allowZero ? this.inputValue === '0' : false)) {
        this.inputValue = '';
        this.onChange(null);
      } else {
        this.inputValue = <any>(Number(this.inputValue));
        this.onChange(this.inputValue);
        this.inputValue = this.currencyPipe.transform(Number(this.inputValue));
      }
    } else if (this.type === InputType.NUMBER) {
      this.onChange(this.inputValue);
    } else {
      this.onChange(this.inputValue);
    }
  }

  onPaste(event: any): void {
    let data;

    if ((<any>window).clipboardData && (<any>window).clipboardData.getData) // IE
      data = (<any>window).clipboardData.getData('Text');
    else data = (event.originalEvent || event).clipboardData.getData('text/plain');

    if (this.type === InputType.MONEY || this.type === InputType.NUMBER) {
      const replacedValue = data.replace(/\D/gm, '');
      if (replacedValue !== '') {
        const numberValue = Number(replacedValue);
        this.inputValue = replacedValue;
        this.onChange(numberValue);
      }
      event.preventDefault();
     }
  }

  onKeyPress(event: any): void {
    const pattern =  /[0-9.]/ ;
    // eslint-disable-next-line no-useless-escape
    const patternFirst = /[0-9\-]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (this.type === InputType.MONEY || this.type === InputType.NUMBER) {
      if ((this.inputValue + '').indexOf('.') > -1 && inputChar === '.') // Dirty Hack
        event.preventDefault();

      if (this.inputRef.nativeElement.selectionStart !== 0 || !this.allowNegative) {
        if (!pattern.test(inputChar))
          event.preventDefault();
      } else if (this.allowNegative) {
        if (!patternFirst.test(inputChar))
          event.preventDefault();
      }
    }

    this.onChange(this.inputValue);
  }

  finalize(): void {
    this.valueChangedFlag = false;
    // Propagate Changes to ngModel
    this.onChange(this.inputValue);
    this.onValueTouched.emit(this.valueChangedFlag);

    if (this.showButton) {
      // Loose Focus
      if (this.inputRef.nativeElement) {
        // noinspection TypeScriptValidateJSTypes
        this.inputRef.nativeElement.blur();
      }
    }

  }

  onShadowCopy(value: string): void {
    if (this.type === InputType.MONEY) {
      this.shadowCopy = this.currencyPipe.transform(value);
    } else this.shadowCopy = value;
  }

  getShadowCopy(): string {
    return this.shadowCopy;
  }

  focusOut(): void {
    if (!this.showButton) {
      this.accept();
    }
  }
}
